import React, { useState, useEffect } from 'react';
import { Search, AlertCircle, RefreshCw } from 'lucide-react';
import workflowTemplateService from '../../../services/workflowTemplateService';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';



const TemplateLibrary = ({ onTemplateSelect, onTemplateEdit, isCollapsed, onToggleCollapse }) => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [expandedTemplates, setExpandedTemplates] = useState(new Set());
  const MAX_RETRIES = 3;

  // Fetch templates with retry logic
  const fetchTemplates = async (isRetry = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Add a small delay for retries to allow schema cache to refresh
      if (isRetry && retryCount > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
      }
      
      const data = await workflowTemplateService?.getAllTemplates();
      setTemplates(data);
      setFilteredTemplates(data);
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      console.error('Error loading templates:', err);
      
      // Check if it's a schema cache error
      const isSchemaError = err?.message?.includes('schema cache') || err?.message?.includes('PGRST205');
      
      if (isSchemaError && retryCount < MAX_RETRIES) {
        // Automatically retry
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchTemplates(true), 1000);
      } else {
        // Show error after max retries or for other errors
        setError(err?.message || 'Failed to load templates');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Handle search and filter
  useEffect(() => {
    const matchesSearch = template => {
      const name = template?.name?.toLowerCase();
      const description = template?.description?.toLowerCase();
      return searchQuery?.toLowerCase()?.split(' ')?.every(word => name?.includes(word) || description?.includes(word));
    };

    const matchesCategory = template => {
      return selectedCategory === 'all' || template?.category === selectedCategory;
    };

    setFilteredTemplates(templates?.filter(template => matchesSearch(template) && matchesCategory(template)));
  }, [searchQuery, selectedCategory, templates]);

  // Handle template drag start
  const handleDragStart = (e, template) => {
    e.dataTransfer.effectAllowed = 'copy';
    e?.dataTransfer?.setData('application/json', JSON.stringify({
      type: 'template',
      template: template
    }));
  };

  const toggleTemplateExpand = (templateId) => {
    setExpandedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(templateId)) {
        newSet?.delete(templateId);
      } else {
        newSet?.add(templateId);
      }
      return newSet;
    });
  };

  const handleDeleteTemplate = async (templateId, e) => {
    e?.stopPropagation();
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await workflowTemplateService?.deleteTemplate(templateId);
        setTemplates(prev => prev?.filter(t => t?.id !== templateId));
      } catch (err) {
        console.error('Error deleting template:', err);
        setError(err?.message || 'Failed to delete template');
      }
    }
  };

  // Get unique categories
  const categories = ['all', ...new Set(templates?.map(t => t?.category).filter(Boolean))];

  // Manual retry handler
  const handleRetry = () => {
    setRetryCount(0);
    fetchTemplates();
  };

  if (isCollapsed) {
    return (
      <div className="w-16 h-full bg-card border-r border-border flex flex-col items-center py-4 gap-4">
        <button
          onClick={onToggleCollapse}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 transition-colors duration-250"
          aria-label="Expand template library"
        >
          <Icon name="ChevronRight" size={20} />
        </button>
        <button
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-primary hover:text-primary-foreground transition-all duration-250"
          title="Templates"
        >
          <Icon name="Layers" size={20} />
        </button>
      </div>
    );
  }

  if (loading && retryCount === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading templates...</p>
        </div>
      </div>
    );
  }

  if (loading && retryCount > 0) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Retrying... (Attempt {retryCount}/{MAX_RETRIES})</p>
          <p className="text-sm text-gray-500 mt-1">Waiting for database schema to refresh</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isSchemaError = error?.includes('schema cache') || error?.includes('PGRST205');
    
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {isSchemaError ? 'Database Schema Not Ready' : 'Error Loading Templates'}
          </h3>
          <p className="text-gray-600 mb-4">
            {isSchemaError 
              ? 'The workflow templates table was recently created. The database API needs to refresh its schema cache.'
              : error
            }
          </p>
          <div className="space-y-2">
            <Button 
              onClick={handleRetry}
              className="w-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry Loading
            </Button>
            {isSchemaError && (
              <p className="text-sm text-gray-500">
                If the issue persists, try refreshing the page or wait a minute for the schema cache to update automatically.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-card border-r border-border flex flex-col">
      <div className="flex items-center justify-between px-4 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Layers" size={20} color="var(--color-primary)" />
          <h2 className="text-lg font-semibold text-foreground">Templates</h2>
        </div>
        <button
          onClick={onToggleCollapse}
          className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted hover:bg-muted/80 transition-colors duration-250"
          aria-label="Collapse template library"
        >
          <Icon name="ChevronLeft" size={18} />
        </button>
      </div>

      <div className="px-4 py-3 border-b border-border space-y-3">
        <Input
          type="search"
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          className="w-full"
        />
        
        <div className="flex flex-wrap gap-2">
          {categories?.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-250 ${
                selectedCategory === cat
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <span>{cat}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
            <p className="text-sm text-destructive mt-2 text-center">{error}</p>
            <button
              onClick={handleRetry}
              className="mt-4 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : filteredTemplates?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
            <p className="text-sm text-muted-foreground mt-2">No templates found</p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mt-2 text-xs text-primary hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTemplates?.map(template => {
              const isExpanded = expandedTemplates?.has(template?.id);
              const nodeCount = template?.nodes?.length || 0;
              const connectionCount = template?.connections?.length || 0;

              return (
                <div
                  key={template?.id}
                  className="bg-muted rounded-lg overflow-hidden hover:shadow-md transition-all duration-250"
                >
                  <div
                    draggable
                    onDragStart={(e) => handleDragStart(e, template)}
                    className="p-3 cursor-grab active:cursor-grabbing hover:bg-muted/80 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium text-foreground truncate">
                            {template?.name}
                          </h4>
                          {template?.is_system && (
                            <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded">
                              System
                            </span>
                          )}
                        </div>
                        {template?.description && (
                          <p className="caption text-muted-foreground mt-1 line-clamp-2">
                            {template?.description}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Icon name="Box" size={12} />
                            {nodeCount} nodes
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="Link" size={12} />
                            {connectionCount} connections
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            if (template?.is_system) {
                              alert('System templates cannot be edited. Please create a copy of this template to make changes.');
                              return;
                            }
                            onTemplateEdit?.(template);
                          }}
                          className={`p-1.5 rounded-md transition-colors ${
                            template?.is_system 
                              ? 'opacity-50 cursor-not-allowed hover:bg-muted' :'hover:bg-primary/10 text-primary cursor-pointer'
                          }`}
                          aria-label="Edit template nodes"
                          title={template?.is_system ? 'System templates cannot be edited' : 'Edit Nodes'}
                          disabled={template?.is_system}
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        {!template?.is_system && (
                          <button
                            onClick={(e) => handleDeleteTemplate(template?.id, e)}
                            className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive transition-colors"
                            aria-label="Delete template"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-3 pb-3 pt-0 border-t border-border/50">
                      <div className="mt-2 space-y-2">
                        <div className="text-xs font-medium text-foreground">Nodes in this template:</div>
                        <div className="space-y-1">
                          {template?.nodes?.map((node, idx) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 px-2 py-1.5 bg-background rounded text-xs"
                            >
                              <div
                                className="flex items-center justify-center w-6 h-6 rounded flex-shrink-0"
                                style={{ backgroundColor: `${node?.color}20`, color: node?.color }}
                              >
                                <Icon name={node?.icon} size={12} />
                              </div>
                              <span className="text-foreground truncate">{node?.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-border">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="Info" size={14} />
          <span>Drag templates to canvas</span>
        </div>
      </div>
    </div>
  );
};

export default TemplateLibrary;